import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import InvestForm from '../components/Invest/InvestForm';
import Card from '../components/Common/Card';
import PageLoader from '../components/Loader/PageLoader';
import { formatUSDT, bpsToPercent } from '../utils/format';
import { PLAN_INFO } from '../config/contract';

const Invest = () => {
  const { contract, account, userId, refreshUserId } = useWeb3();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [limits, setLimits] = useState({ min: '0', multiple: '0' });
  const [fees, setFees] = useState({ claim: 0, early: 0 });
  const [usdtBalance, setUsdtBalance] = useState('0');
  const [reloadKey, setReloadKey] = useState(0);

  const { usdtContract } = useWeb3();

  const params = new URLSearchParams(location.search);
  const refUrl = params.get('ref') || '';

  const load = async () => {
    if (!contract) {
      setLoading(false);
      return;
    }
    try {
      const [limitsArr, feesArr, bal] = await Promise.all([
        contract.getInvestmentLimits(),
        contract.getFees(),
        usdtContract && account ? usdtContract.balanceOf(account) : Promise.resolve(0)
      ]);
      setLimits({ min: limitsArr[0].toString(), multiple: limitsArr[1].toString() });
      setFees({
        claim: feesArr._claimFee?.toString() || feesArr[2]?.toString() || '0',
        early: feesArr._earlyWithdrawFee?.toString() || feesArr[3]?.toString() || '0'
      });
      setUsdtBalance(bal.toString ? bal.toString() : '0');
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [contract, account, reloadKey]);

  if (loading) return <PageLoader text="Loading Plans" />;

  return (
    <div>
      <h1 className="page-title">Invest</h1>
      <p className="page-subtitle">Stake USDT and earn daily ROI on BNB Chain</p>

      <div className="invest-layout">
        <InvestForm
          minInvestment={limits.min}
          multiple={limits.multiple}
          isRegistered={!!userId}
          refUrlId={refUrl}
          onSuccess={() => { setReloadKey((k) => k + 1); refreshUserId(); }}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Card title="Your Balance">
            <div style={{
              fontFamily: 'JetBrains Mono',
              fontSize: 28,
              fontWeight: 700,
              color: 'var(--accent-cyan)',
              textShadow: '0 0 20px rgba(0, 212, 255, 0.4)'
            }}>
              {formatUSDT(usdtBalance)} <span className="stat-suffix">USDT</span>
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
              Available in wallet
            </div>
          </Card>

          <Card title="All Tiers">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {PLAN_INFO.map((p) => (
                <div key={p.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 0',
                  borderBottom: '1px solid var(--border-secondary)'
                }}>
                  <div>
                    <div style={{
                      fontFamily: 'Orbitron, sans-serif',
                      fontSize: 13,
                      fontWeight: 800,
                      letterSpacing: '0.1em',
                      color: p.color,
                      textShadow: `0 0 10px ${p.color}80`
                    }}>{p.rank}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono' }}>
                      {p.days}d · Daily {p.dailyROI}
                    </div>
                  </div>
                  <div style={{
                    fontFamily: 'JetBrains Mono',
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--accent-green)'
                  }}>{p.totalROI}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Fees">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Claim Fee</span>
                <span style={{ fontFamily: 'JetBrains Mono', color: 'var(--accent-pink)' }}>
                  {bpsToPercent(fees.claim)}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Early Withdraw</span>
                <span style={{ fontFamily: 'JetBrains Mono', color: 'var(--accent-pink)' }}>
                  {bpsToPercent(fees.early)}%
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Invest;
